// Copyright (c) 2026 杨翔宇（武汉文理学院）
// 物理智能教学智能体平台 (Physics Intelligent Agent)
// 保留所有权利 (All Rights Reserved)。未经著作权人书面许可，
// 不得以任何形式复制、传播、修改或用于商业用途。

/**
 * CD-14 frontend operational-status authority.
 *
 * The `/api/health` and `/api/maintenance` path literals previously lived
 * inline in call sites (`useOnlineStatus.ts` called `fetch('/api/health')`
 * directly; `App.tsx` inlined the `/api/maintenance` literal).  This module is
 * the single frontend authority that owns those two operational paths so the
 * hooks/App layer stops hardcoding them.
 *
 * `fetchOptionalJson` stays the low-level optional-GET transport helper; only
 * the path literals + probe semantics move behind this seam.
 */
import { fetchOptionalJson } from './runtimeSupportFetch';
import type { OptionalFetchResult } from './runtimeSupportFetch';

/** Single owned literal for the runtime health endpoint. */
export const RUNTIME_HEALTH_PATH = '/api/health';
/** Single owned literal for the maintenance-status endpoint. */
export const MAINTENANCE_STATUS_PATH = '/api/maintenance';

export interface RuntimeHealthProbeResult {
  ok: boolean;
}

/**
 * Connectivity probe for the runtime health endpoint.
 *
 * Mirrors the prior inline behaviour: a HEAD request first (cheapest), with a
 * GET fallback for proxies/middleware that do not handle HEAD consistently.
 * Honours a caller-supplied AbortSignal so the connectivity loop in
 * `useOnlineStatus` keeps its timeout/cancel semantics.
 */
export async function probeRuntimeHealth(
  signal?: AbortSignal
): Promise<RuntimeHealthProbeResult> {
  let response = await fetch(RUNTIME_HEALTH_PATH, {
    method: 'HEAD',
    cache: 'no-store',
    signal,
  });

  if (!response.ok) {
    response = await fetch(RUNTIME_HEALTH_PATH, {
      method: 'GET',
      cache: 'no-store',
      signal,
    });
  }

  return { ok: response.ok };
}

export interface MaintenanceStatus {
  active?: boolean;
  message?: string;
  [key: string]: unknown;
}

/**
 * §3.5（frontend-deep-optimization 计划）：无 signal 的探测请求做进程内去重——
 * React.StrictMode 下 useEffect 双执行时并发触发只发一次网络请求
 * （App.tsx 挂载探测场景）；带 signal 的调用保持原有取消语义，不参与去重。
 */
let maintenanceInflight: Promise<OptionalFetchResult<MaintenanceStatus>> | null = null;

export async function fetchMaintenanceStatus(
  options?: { signal?: AbortSignal }
): Promise<OptionalFetchResult<MaintenanceStatus>> {
  if (options?.signal) {
    return fetchOptionalJson<MaintenanceStatus>(MAINTENANCE_STATUS_PATH, options);
  }
  if (!maintenanceInflight) {
    maintenanceInflight = fetchOptionalJson<MaintenanceStatus>(MAINTENANCE_STATUS_PATH);
    void maintenanceInflight.finally(() => {
      maintenanceInflight = null;
    });
  }
  return maintenanceInflight;
}
